import React, { FC, useEffect, useMemo, useState } from "react";
import "./refund.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import {
  checkRefundStatusApi,
  refundPayment,
} from "../../../../services/payment";
import { toast } from "react-toastify";
import { ICheckRefund, IConcessionData, ITicketData } from "../../../../models/payment";
import { getPaymentMethod } from "../../../../services/payment";
import { TICKET_FLOW } from "../../../../constants/ticket";
import { MODAL_TYPE, TICKET_FLOW_TYPES } from "../../../../models/tickets";
import { setModal, setRefundDetails } from "../../../../store/ticketBooking/action";
import { IMAGE_URL_CONFIG } from "../../../../constants/url";
import { formatRefundConcession, formatRefundTicket } from "../../../../utils/tickets";
import { COUNTRY } from "../../../../constants/app";

interface IRefundTicketProps {
  onCloseModal: () => void;
}

const REFUND_TYPE = {
  FULL: 'full',
  PARTIAL: 'partial',
}

const Refund: FC<IRefundTicketProps> = ({ onCloseModal }) => {
  const { countryId, refundDetails } = useSelector(
    (state: RootState) => ({
      countryId: state.applicationReducer.countryId,
      refundDetails: state.ticketBookingReducer.refundDetails
    })
  );

  const dispatch = useDispatch();
  const [refundData, setRefundData] = useState<ICheckRefund | null>(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [refundType, setRefundType] = useState('');
  const [refundItems, setRefundItems] = useState<any>({
    ticketInput: [],
    concessionInput: []
  });
  const [refundedItems, setRefundedItems] = useState<any>({
    ticketInput: [],
    concessionInput: []
  });
  const [bookingFee, setBookingFee] = useState(0);

  const extractJson = (extra: any) => {
    return JSON.parse(extra);
  }

  const checkRefund = async (sharedUserSessionId: string) => {
    setLoading(true);
    const {
      data: { data },
    } = await checkRefundStatusApi({
      saleId: sharedUserSessionId,
      countryId: countryId,
    });
    setLoading(false);
    if (data && data.status) {
      setRefundData({
        ...data,
        ticketData: data.ticketData.filter((v: any) => v.refundstatus !== 'refund'),
        concessionData: data.concessionData.filter((v: any) => v.refundstatus !== 'refund')
      });
      setRefundedItems({
        ...data,
        ticketInput: data.ticketData.filter((v: any) => v.refundstatus === 'refund'),
        concessionInput: data.concessionData.filter((v: any) => v.refundstatus === 'refund')
      });
      setBookingFee(Number(data.bookingFee) / Number(data?.ticketData?.length));
    } else {
      toast.error(data.message);
      setTimeout(() => {
        onCloseModal();
      }, 1000)
    }
  };

  const hasGl = useMemo(() => {
    if (refundData) {
      return refundData?.concessionData?.filter(v => {
        if (v.collect_type === "service-seat") {
          const seatNo = extractJson(v.Extra)?.deliveryInfo?.SeatNumber;
          return refundItems.ticketInput.findIndex((v: any) => `${v.SeatRowId}${v.SeatNumber}` === seatNo) > -1 ? true : false
        }
        return false;
      }).length! > 0 ? true : false
    }
    return false;
  }, [refundData, refundItems])

  const processRefund = async () => {
    if (totalRefundAmount <= 0) {
      toast.error('Please select the item to refund')
      return;
    }

    const payload: any = {
      amount: countryId === COUNTRY.NZ? totalRefundAmount: parseInt(
        (
          (totalRefundAmount) *
          100
        ).toFixed()
      ),
      countryId: countryId,
      txnType: 4,
      requestType: "refundPayment",
      paymentMethod: getPaymentMethod(countryId),
      txnID: refundData?.txnId,
      transactionReference: refundData?.transactionRefNo,
      saleId: refundDetails?.saleId,
      refundType: "individual",
      partialRefund: refundType === REFUND_TYPE.PARTIAL,
      ticketInput: refundItems.ticketInput,
      concessionInput: refundItems.concessionInput
    };
    dispatch(setRefundDetails({
      payload,
      refundItems,
      refundData,
      saleId: refundDetails?.saleId,
      refundType,
      bookingFee: bookingFee,
    }))
    setRefundItems({
      ticketInput: [],
      concessionInput: []
    })
    setRefundedItems({
      ticketInput: [],
      concessionInput: []
    })
    if (hasGl && refundType === REFUND_TYPE.PARTIAL) {
      const modal =
        TICKET_FLOW[TICKET_FLOW_TYPES.REFUND][MODAL_TYPE.REFUND_RECONFIRMATION];
      dispatch(
        setModal({
          ...modal,
          type: MODAL_TYPE.REFUND,
        })
      );
    } else {
      const { status, data } = (await refundPayment(payload)) as any;
      if (
        status &&
        data &&
        data.data &&
        (data.data.message === "Approved" || data.data.message === "Refund initiated")
      ) {
        const modal =
          TICKET_FLOW[TICKET_FLOW_TYPES.REFUND][MODAL_TYPE.REFUND_CONFIRMATION];
        dispatch(
          setModal({
            ...modal,
            type: MODAL_TYPE.REFUND
          })
        );
      } else {
        toast.error("Error occured please try later");
      }
    }
  };

  const handleRefundOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event
    setRefundItems({
      ticketInput: [],
      concessionInput: []
    })
    setRefundType(value);
    checkRefund(refundDetails?.saleId);
  };

  useEffect(() => {
    if (refundDetails?.saleId && refundDetails?.refundType) {
      checkRefund(refundDetails?.saleId);
      setRefundType(refundDetails?.refundType);
    }
  }, [refundDetails?.saleId])

  const onBack = () => {
    setRefundType("");
  }

  useEffect(() => {
    if (refundType === REFUND_TYPE.FULL && refundData) {
      setRefundItems({
        ticketInput: refundData?.ticketData?.filter((v:ITicketData)=>v.refundstatus === null).map((v: ITicketData) => {
          return formatRefundTicket(v);
        }),
        concessionInput: refundData?.concessionData?.filter((v:IConcessionData)=> v.refundstatus === null).map((v: IConcessionData) => {
          return formatRefundConcession(v) 
        })
      })
    } else {
      setRefundItems({
        ticketInput: [],
        concessionInput: []
      })
    }
  }, [refundType, refundData])

  const totalRefundAmount = useMemo(() => {
    return (refundItems.ticketInput.reduce((acc: number, v: ITicketData) => {
      acc = acc + v.Ticket_price
      return acc;
    }, 0)) + (refundItems.concessionInput.reduce((acc: number, v: IConcessionData) => {
      acc = acc + v.Price
      return acc;
    }, 0))
  }, [refundItems])

  const totalRefundedAmount = useMemo(() => {
    return (refundedItems.ticketInput.reduce((acc: number, v: ITicketData) => {
      acc = acc + (v.Ticket_price + (v.bookingFeeRefunded?bookingFee:0))
      return acc;
    }, 0)) + (refundedItems.concessionInput.reduce((acc: number, v: IConcessionData) => {
      acc = acc + v.Price
      return acc;
    }, 0))
  }, [refundedItems, bookingFee])


  const onClickItem = (event: any, v: any, type: string) => {
    const value = type === 'ticketInput' ? formatRefundTicket(v) : formatRefundConcession(v)
    const { target: { checked } } = event;
    const items = { ...refundItems };
    v.checked = checked;
    if (checked) {
      items[type] = [...refundItems[type], value];
      setRefundItems(items);
    } else {
      const index = refundItems[type].findIndex((v: any) => v.RowId === value.RowId);
      if (index > -1) {
        items[type].splice(index, 1);
        setRefundItems(items);
      }
    }
  }

  const TotalRefund = () => {
    return <div className="summary">
      <div className="summary-box">
        <div className="summary-item first">
          <div className="label">TOTAL PURCHASE</div>
          <div className="value bold">$ {!loading ? (
            Number(refundData?.totalAmount) -
            Number(refundData?.bookingFee)
          ).toFixed(2) : 0}</div>
          <div className="subtext">Excluding Booking Fees</div>
        </div>
        <div className="summary-item">
          <div className="label">BOOKING FEE(S)</div>
          <div className="value bold">$ {!loading ? Number(refundData?.bookingFee).toFixed(2) : 0}</div>
        </div>
        {totalRefundedAmount > 0 ? <div className="summary-item">
          <div className="label">PREVIOUSLY REFUNDED</div>
          <div className="value bold">$ {totalRefundedAmount.toFixed(2)}</div>
        </div> : null}
        <div className="summary-item">
          <div className="label">TOTAL TO REFUND</div>
          <div className={`value refund-total ${totalRefundAmount == 0 ? 'disable' : ''}`}>$ {(totalRefundAmount).toFixed(2)}</div>
        </div>
      </div>
    </div>
  }


  const PartialRefund = () => {
    return <div className="part-of-my-purchase">
      <div className="refund-items">
        <h4>Select item(s) to refund:</h4>
        <ul>
          {refundData?.ticketData && refundData?.ticketData?.map((item, i) => (
            <>
              <li key={i} className="refund-item">
                <label>
                  <input type="checkbox" checked={item.checked} onClick={(event) => !item.refundstatus && onClickItem(event, item, 'ticketInput')} />
                  <div className="item-info">
                    <div className="item-title">Seat {item.Seat_number}</div>
                    <div className=""> <hr /></div>
                    <div className="item-sub">Excluding $ {bookingFee?.toFixed(2)} Booking Fee (Non-Refundable)</div>
                  </div>
                  <div className="item-price">$ {item.Ticket_price.toFixed(2)}</div>
                </label>
              </li>
              {i !== (Number(refundData?.ticketData?.length) + Number(refundData?.concessionData?.length) - 1) && (
                <div className="hr-bottom"><hr /></div>
              )}
            </>
          ))}
          {refundData?.concessionData && refundData?.concessionData?.map((item, i) => (
            <>
              <li key={i} className="refund-item">
                <label>
                  <input type="checkbox" checked={item.checked} onClick={(event) => !item.refundstatus && onClickItem(event, item, 'concessionInput')} />
                  <div className="item-info">
                    <div className="item-title">{item.Description}</div>
                    <div className="item-sub">{extractJson(item.Extra)?.modifier?.join(',')}</div>
                    <div className=""> <hr /></div>
                    <div className="item-sub">{extractJson(item.Extra)?.deliveryInfo?.DeliveryOption === 1 ? `Deliver to Seat ${extractJson(item.Extra)?.deliveryInfo?.SeatNumber} - ${extractJson(item.Extra)?.deliveryInfo?.Comment} of movie` : 'Self collect from counter'}</div>
                  </div>
                  <div className="item-price">{item.Price.toFixed(2)}</div>
                </label>
              </li>
              {i !== Number(refundData?.concessionData?.length) - 1 && (
                <div className="hr-bottom"><hr /></div>
              )}
            </>
          ))}
          {refundData?.ticketData?.length! + refundData?.concessionData?.length! === 0 ? <li className="refund-item">No Item(s) to refund</li> : null}
        </ul>
      </div>
    </div>
  }

  const PreviousRefund = () => {
    return <>
      <div className="refunded-header">
        <h4>{refundedItems?.ticketInput?.length + refundedItems?.concessionInput?.length} previously refunded item</h4>
        <button onClick={() => setIsVisible(!isVisible)}>
          {isVisible ? <span>HIDE <img className="black-arrow" src={IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH + 'arrow_up_black.svg'} />
          </span> : <span>SHOW <img className="black-arrow hide" src={IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH + 'arrow_up_black.svg'} /> </span>}
        </button>
      </div>

      <div className={`refunded-body ${isVisible ? "visible" : "hidden"}`}>
        {refundedItems?.ticketInput?.map((v: any) =>
          <div className="refunded-card">
            <div className="left">
              <img src={IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH + 'tick_green.svg'} />
              <div className="checkmark"></div>
            </div>
            <div className="middle">
              <div className="title">Seat {v.Seat_number}</div>
              <div className=""> <hr /></div>
               {v?.bookingFeeRefunded?<div className="item-sub">Including $ {bookingFee?.toFixed(2)} Booking Fee (Refunded By Staff)</div>:
              <div className="item-sub red">Excluding ${bookingFee?.toFixed(2)} Booking Fee (Non-Refundable)</div>}
            </div>
            <div className="right">
              <div className="price">$ {(v.Ticket_price+(v?.bookingFeeRefunded?bookingFee:0)).toFixed(2)}</div>
            </div>
          </div>)}
        {refundedItems?.concessionInput?.map((v: any) =>
          <div className="refunded-card">
            <div className="left">
              <img src={IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH + 'tick_green.svg'} />
              <div className="checkmark"></div>
            </div>
            <div className="middle concession">
              <div className="title">{v.Description}</div>
              <div className="desc">{extractJson(v.Extra)?.modifier?.join(',')}</div>
              <hr />
              <div className="sub">{extractJson(v.Extra)?.deliveryInfo?.DeliveryOption === 1 ? `Deliver to Seat ${extractJson(v.Extra)?.deliveryInfo?.SeatNumber} - ${extractJson(v.Extra)?.deliveryInfo?.Comment} of movie` : 'Self collect from counter'}</div>
            </div>
            <div className="right concession">
              <div className="price">$ {v.Price.toFixed(2)}</div>
            </div>
          </div>)}
      </div>
    </>
  }

  const noRefund = useMemo(() => {
    return refundData?.ticketData?.length! + refundData?.concessionData?.length! === 0
  }, [refundData])

  return (
    <div className="refund-wrapper">
      <div></div>
      <div className="col-12 px-2">
        <div className="refund-options">
          <h4>I want to refund:</h4>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="refundOption"
                value={REFUND_TYPE.FULL}
                checked={refundType === REFUND_TYPE.FULL}
                onChange={handleRefundOptionChange}
              />
              My full purchase
            </label>

            <label>
              <input
                type="radio"
                name="refundOption"
                value={REFUND_TYPE.PARTIAL}
                checked={refundType === REFUND_TYPE.PARTIAL}
                onChange={handleRefundOptionChange}
              />
              Part of my purchase
            </label>
          </div>
          <div className="note">
            <img className="warning-icon" src={IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH + 'white_warning_circle.svg'} />
            Booking fees are non-refundable. Refund will be paid to the original payment method.
          </div>
        </div>
        {refundType === REFUND_TYPE.PARTIAL && !loading ? <PartialRefund /> : null}
        {totalRefundedAmount > 0 && refundType ? <PreviousRefund /> : null}
        {refundType ? <TotalRefund /> : null}

        <div className={`movie_footer_btn_el col-md-12 ml-auto`}>
          <div className="movie_button_wrap justify-content-between">
            <div className="cancel" onClick={onCloseModal}>CANCEL</div>
            <div className="wrap-btn">
              {refundType ? <button
                type="button"
                className="btn gray_btn"
                onClick={onBack}
              >
                <img src={IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH + 'arrow_white_left.svg'} />
                <span>Back</span>
              </button> : null}
              {!noRefund ? <button
                type="button"
                className="ref-btn btn black_btn"
                onClick={totalRefundAmount && processRefund}
                disabled={!totalRefundAmount}
              >
                <img className="white-tick" src={IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH + 'tick_white_small.svg'} />
                <span>PROCESS REFUND</span>
              </button> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Refund;
